import { Component, ViewChild, ElementRef, Output, EventEmitter, Input, Injector, ViewContainerRef, ChangeDetectorRef } from "@angular/core";
import { OdnrMapPoint } from "../../models/odnr-map-point.model";
import { Renderer, MAP_ELEMENT, CONFIG, OVERLAY_CONTAINER } from "./renderer/renderer.interface";
import { of, Observable } from "rxjs";
import { tap, mergeMap, concatMap } from "rxjs/operators";
import { Mode } from "../../models/odnr-map-mode.model";
import esri = __esri;
import { GisLayerService } from "../../services/gis-layer.service";
import { GisViewService } from "../../services/gis-view.service";
import { AccessPlan } from "../../models/access-plan.model";
import { GisMapHelpersService } from "../../services/gis-map-helpers.service";
import { RendererTypes } from "../../../builder/components/state/controls/config/data/well-related/map-control-data-config.component";
import { Renderer2D } from "./renderer/Renderer-2D.service";
import { Renderer3D } from "./renderer/renderer-3D.service";
import { CasingPlanSceneRenderer3D, CasingPlanSceneRenderer2D } from "./renderer/casingPlanSceneRenderer";
import { AreaOfReviewMapRenderer2D } from "./renderer/areaOfReviewRenderer";
import { RouteMapRenderer2D } from "./renderer/routeMapRenderer";


@Component({
    selector: "odnr-map",
    templateUrl: "./odnr-map.component.html",
    styleUrls: ["./odnr-map.component.scss"],
    providers: [GisLayerService, GisViewService, GisMapHelpersService],
    standalone: false
})
export class OdnrMapComponent {
    //to be deleted

    _enabled: boolean = true;

    _accessPlan!: AccessPlan;
    @Input() set accessPlan(value: AccessPlan) {
        this._accessPlan = value;
        if (this._renderer instanceof RouteMapRenderer2D) {
            (this._renderer as RouteMapRenderer2D)._plan = value;
        }
    }
    get accessPlan(): AccessPlan {
        return this._accessPlan;
    }
    @Input()
    casingPlan: any;
    @Input() set enabled(value: boolean) {
        if (value !== this._enabled) {
            this.config = this._config;
            this.draw();
        }
        this._enabled = value;
    }
    get enabled(): boolean {
        return this._enabled;
    }
    @Input()
    latitude;
    @Input()
    longitude;
    @Input()
    coordinateSystem
    @Input()
    showWidgets;
    @Input()
    reviewPoints;
    @Input()
    displayMode;
    clickLocation: OdnrMapPoint;
    @Input() zoom: number = 18;
    public _mode: Mode;
    @Input()
    set mode(val: Mode) {
        this._mode = val;
        let injector = this._createRendererInjector();

        if (!injector.get(MAP_ELEMENT))
            return;
        //TO DO: Switch these to Renderer2D and Renderer3D and create services for rendering casing plans to maps
        switch (this._mode) {
            case Mode.View:
                this._renderer = new CasingPlanSceneRenderer2D(injector);
                break;
            case Mode.Scene:
                this._renderer = new CasingPlanSceneRenderer3D(this.casingPlan, injector);
                break;
            case Mode.AreaOfReview:
                this._renderer = new AreaOfReviewMapRenderer2D(this.reviewPoints, injector);
                break;
            case Mode.Route:
                this._renderer = new RouteMapRenderer2D(this.accessPlan, injector, this.enabled);
                break;
            default:
                this._renderer = new CasingPlanSceneRenderer2D(injector);
                break;
        }
    }
    //end

    @ViewChild('viewport', { static: true })
    viewport: ElementRef;
    @ViewChild('overlay', { read: ViewContainerRef, static: true })
    overlay: ViewContainerRef;
    _config: any;

    @Input()
    set config(value: any) {
        this._config = value;
        let injector = this._createRendererInjector();

        if (!injector.get(MAP_ELEMENT) || !value)
            return;
        switch (this._config.rendererType) {
            case RendererTypes.View:
                this._renderer = new Renderer2D(injector);
                break;
            case RendererTypes.Scene:
                this._renderer = new Renderer3D(injector);
                break;
            case RendererTypes.CasingPlan:
                this._renderer = new CasingPlanSceneRenderer3D(this.casingPlan, injector);
                break;
            case RendererTypes.AreaOfReview:
                this._renderer = new AreaOfReviewMapRenderer2D(this.reviewPoints, injector);
                break;
            case RendererTypes.Route:
                this._renderer = new RouteMapRenderer2D(this.accessPlan, injector, this.enabled);
                break;
            default:
                break;
        }
    }

    @Input() disable: boolean = false;
    @Output() mapLoaded = new EventEmitter<Renderer<esri.SceneView | esri.MapView>>();
    public _renderer: Renderer<esri.SceneView | esri.MapView>;

    constructor(public _injector: Injector, public changeDetector: ChangeDetectorRef) {
    }

    ngAfterViewInit() {
        this.draw();
    }

    private draw(): void {
        if (this._renderer) {
            let mapObs: Observable<esri.SceneView | esri.MapView>;
            let map = this._renderer.initialize() as any;
            if (typeof (map.subscribe) !== 'function') {
                mapObs = of(map);
            } else {
                mapObs = map;
            }
            mapObs.pipe(
                mergeMap(result => {
                    return this._renderer.render();
                }),
                tap((result) => {
                    const legend = document.getElementsByClassName("esri-widget--panel");
                    if (legend && legend.length > 0) {
                        const elem = <HTMLElement>legend[0];
                        elem.style.maxWidth = "200px";
                    }
                    return result;
                })).subscribe((result) => {
                    this.mapLoaded.emit(this._renderer);
                }, (error) => {
                    console.error(error);
                });
        }        
    }

    public _createRendererInjector(): Injector {
        let data = this._config ? this._config : {
            widgets: this.showWidgets,
            center: {
                longitude: this.longitude,
                latitude: this.latitude
            },
            zoom: this.zoom
        };

		return Injector.create({
			providers: [
				{ provide: MAP_ELEMENT, useValue: this.viewport.nativeElement },
				{ provide: OVERLAY_CONTAINER, useValue: this.overlay },
				{ provide: CONFIG, useValue: data }
			],
			parent: this._injector
		});
    }
}


